<template>
  <div :id="id + '_fs'" :class="_fs_class">
    <cat-label
      :label-id="id + '_lbl'"
      :for-id="textboxId"
      :label="label"
      :optional="!mandatory"
      :placeholder="offscreenLabel"
      :errors="errors"
      :readonly="readonly"
    ></cat-label>
    <cat-help-trigger
      v-if="help && help.url"
      :source-id="id"
      :url="help.url"
      :title="label"
    />
    <cat-hint :id="id + '_hint'" :text="hint" />
    <cat-help
      v-if="help && help.text"
      :id="id"
      :heading="help.title"
      :text="help.text"
      :aria-describedby="label && id ? id + '_lbl' : null"
    />
    <slot></slot>
    <div :class="_holder_class">
      <span class="dropdown-indicator" tabindex="-1" aria-hidden="true"></span>
      <input
        :id="id"
        ref="inputElement"
        type="text"
        :name="id"
        data-selectized="true"
        :aria-describedby="hint ? id + '_hint' : null"
      />
    </div>
    <slot name="after-input"></slot>
    <cat-errors v-if="errors && errors.length > 0" :errors="errors" />
    <div role="presentation" class="spacer"></div>
  </div>
</template>

<script lang="ts">
import jQuery from "jquery";

import "@/plugins/selectize/plugin_a11y.js";
import "@/plugins/selectize/remove_button_a11y.js";
import "@/plugins/selectize/auto_position.js";

import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatLabel from "@/components/utilities/cat-label.vue";
import CatHint from "@/components/utilities/hint.vue";
import CatHelp from "@/components/utilities/help.vue";
import CatHelpTrigger from "@/components/catPageTemplate/catHelpTrigger.vue";
import SelectizeMixin from "./selectize-mixin.js";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);

    return {
      resolveTxt,
      displayParams
    };
  },
  name: "CatAttributeMultiselectList",
  components: {
    CatErrors,
    CatLabel,
    CatHint,
    CatHelp,
    CatHelpTrigger
  },
  mixins: [SelectizeMixin],
  props: {
    id: {
      type: String,
      description: "Assigns an id to the multi select component"
    },
    errors: {
      type: Array,
      description: "Displays multi select error(s)"
    },
    label: {
      type: String,
      description: "Assigns a label to the multi select component."
    },
    items: {
      type: [Object],
      description: "Determines items that can be added to the input field."
    },
    hint: {
      type: String,
      description: "Adds hint text."
    },
    placeholder: {
      type: String,
      description: "Adds placeholder text."
    },
    offscreenLabel: {
      type: String,
      description:
        "Used by screen readers and assistive technologies to provide label and context for accessibility."
    },
    autofillToken: {
      type: String,
      default: "off",
      description:
        "The autofill token to be set on the input text element in the multi select."
    },
    itemId: {
      type: String,
      description: "Adds id to drop down items (list items)."
    },
    value: {
      type: String,
      description: "Determines value of text."
    },
    mandatory: {
      type: Boolean,
      default: false,
      description: "If true field can't be empty"
    },
    size: {
      type: String,
      description:
        "Determines size of the input wrapper (small, medium, large)."
    },
    readonly: {
      type: Boolean,
      description:
        "Renders entries as list items and prevents editing (for configuration)."
    },
    autocompleteSearchingText: {
      type: String,
      description: "Deprecated."
    },
    autocompleteNoResultsText: {
      type: String,
      description: "Deprecated."
    },
    allowNew: {
      type: Boolean,
      description: "Deprecated."
    },
    optionFunction: {
      type: Function,
      description: "."
    },
    itemFunction: {
      type: Function,
      description: "."
    },
    minInputLength: {
      type: Number,
      default: 2,
      description: "Determines minimum input."
    },
    maxItems: {
      type: Number,
      default: null,
      description: "Determines maximum input."
    },
    help: {
      type: Object,
      description: "Determines help url, level and text."
    }
  },
  data: function () {
    return {
      model: this.value ? this.value : "",
      selectize: null,
      textboxId: this.id + "-textbox"
    };
  },
  computed: {
    _fs_class: function () {
      return {
        fieldset: true,
        "cat-select-multi-list": true,
        "cat-select-multi": true,
        list: true,
        required: this.mandatory,
        optional: !this.mandatory,
        "readonly-list": this.readonly
      };
    },
    _holder_class: function () {
      return {
        // 'select-holder': !this.autocomplete,
        "ac-holder": true,
        small: this.size == "small",
        medium: !this.size || this.size == "medium",
        large: this.size == "large"
      };
    }
  },
  watch: {
    value: function (newval, oldvalue) {
      let t = this;
      if (this.model != newval) {
        this.$nextTick(function () {
          t.model = newval;
          t.selectize.clear(true);
          t.selectize.addItems(t.model.split(","), false);
        });
      }
    },
    items: function (newItems, oldItems) {
      this.refreshOptions();
    }
  },

  mounted: function () {
    // const autocompleteFunction = this.autocompleteFunction
    const _this = this;
    const initialOptions = this.items
      ? this.items.map((i) => {
          return _this.getSelectizeListOption(i);
        })
      : [];
    const options = {
      onChange: (val) => {
        // console.log("Change", val);
        const newItems = val.split(",");
        const existingItems = _this.model.split(",");
        var newValues = [];
        var delItems = [];

        newItems.forEach((v) => {
          if (v !== "" && existingItems.indexOf(v) === -1) {
            newValues.push(v);
          }
        });
        existingItems.forEach((v) => {
          if (v !== "" && newItems.indexOf(v) === -1) {
            delItems.push(v);
          }
        });
        newValues.forEach((v) => {
          _this.autocompleteChange(val, v);
        });
        delItems.forEach((v) => {
          _this.autocompleteRemove(val, v);
        });
        this.model = val;
      },
      options: initialOptions,
      items: this.model.split(","),
      delimiter: ",",
      valueField: "value",
      labelField: "text",
      plugins: {
        remove_button_a11y: {
          title: "Remove",
          label: ""
        },
        selectize_a11y: {
          openOnClick: true,
          textboxId: this.textboxId,
          selectItemIds: this.itemId ? [this.itemId] : [this.selectItemId],
          autofillToken: this.autofillToken,
          label: this.label || this.offscreenLabel || this.placeholder,
          labelId: this.label ? this.id + "_lbl" : null,
          id: this.id
        },
        auto_position: true
      },
      persist: false,
      hideSelected: true,
      placeholder: this.placeholder,
      preload: this.minInputLength == 0,
      maxItems: this.maxItems,
      render: {
        option: function (item, escape) {
          var optionId = _this._formatOptionId(item.text);

          return (
            "<div class='option' role='option' id='" +
            optionId +
            "'>" +
            escape(item.text) +
            "</div>"
          );
        },
        item: function (item, escape) {
          return (
            "<div class='item' id='" +
            _this.getSelectItemId(escape(item.text)) +
            "'>" +
            escape(item.text) +
            "</div>"
          );
        }
      }
    };
    if (
      typeof this.optionFunction === "function" ||
      (this.items && this.items.find((el) => el.html || el.lines))
    ) {
      options.render = {
        option: function (data, escape) {
          //console.log("option", data);
          const item = _this.getItemByCode(data.value);
          if (typeof _this.optionFunction === "function") {
            return _this.optionFunction(data, item, escape);
          }
          if (item.html) {
            return item.html;
          }
          if (item.lines) {
            var lines = "<div class='pill_option' role='option'>";
            for (var idx in item.lines) {
              lines += `<div class="pill_line_${idx}">${escape(
                item.lines[idx]
              )}</div>`;
            }
            return lines + "</div>";
          }
          return escape(data.text);
        }
      };
    }
    if (typeof this.itemFunction === "function") {
      if (!options.render) {
        options.render = {};
      }
      options.render.item = function (data, escape) {
        // console.log("item", data);
        const item = _this.getItemByCode(data.value);
        return _this.itemFunction(data, item, escape);
      };
    }
    const s = jQuery(this.$refs.inputElement).selectize(options);
    if (s && s.length > 0) {
      this.selectize = s[0].selectize;
      // Special fix for MS Edge, see discussion here: https://github.com/rstudio/shiny/issues/1903
      if (document.documentMode || /Edge/.test(navigator.userAgent)) {
        const oldOnBlur = this.selectize.onBlur;
        this.selectize.onBlur = function (e, dest) {
          var self = this;
          if (!self.ignoreBlur && e && e.target === self.$control_input[0]) {
            self.ignoreBlur = true;
            self.onFocus(e);
            return;
          }
          return oldOnBlur.call(self, e, dest);
        };
      }
    }
  },
  methods: {
    _onChange: function (values, value, text) {
      // console.log("changed", value, text);
      this.$emit("ui-change", {
        value: value,
        values: values,
        id: this.id ? this.id : "",
        el: this.$el,
        text: text
      });
    },

    _onBlur: function (event) {
      this.$emit("ui-blur", {
        event: event,
        id: this.id
      });
    },
    autocompleteChange: function (values, value, text) {
      // console.log("chg", value, text);
      if (values == this.model) {
        return;
      }
      this.model = values;
      this._onChange(values, value);
    },
    autocompleteRemove: function (values, value) {
      this.$emit("ui-remove", {
        value: value,
        values: values,
        id: this.id ? this.id : "",
        el: this.$el
      });
    },
    autocompleteCreate: function (text) {
      // console.log("create", text);
      if (!this.allowNew || text == this.model) {
        return;
      }

      this.$emit("ui-create", {
        id: this.id ? this.id : "",
        text: text,
        el: this.$el,
        value: text
      });
    },
    getItemByCode: function (code) {
      if (!this.items) {
        return undefined;
      }
      return this.items.find((el) => el.id === code);
    },
    getSelectItemId: function (item) {
      return this.id + item + "-select-item";
    },
    /**
     * This method will refresh the dropdown options and selected items from the content of the props 'items'
     */
    refreshOptions: function () {
      this.selectize.clearOptions(true);
      // Add all the options back in the drop-down
      const t = this;
      this.items.forEach(function (item) {
        t.selectize.addOption(t.getSelectizeListOption(item));
      });
      this.selectize.refreshOptions(false);
    },
    /**
     * Return the option object for the selectize
     * @param item Item object from the props 'items' list
     * @returns {{text: *, value: *}}
     */
    getSelectizeListOption: function (item) {
      return { value: item.id, text: item.text };
    }
  }
});
</script>
